import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { 
  Bell, 
  Clock, 
  AlertTriangle, 
  CheckCircle, 
  X, 
  ChevronRight,
  Inbox,
  Calendar,
  User
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { parseLocalDate } from '../utils/dateHelpers';
import './Buzon.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Buzon = ({ onSelectGestion, onClose }) => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [gestionesAsignadas, setGestionesAsignadas] = useState([]);
  const [tareasVencerCNC, setTareasVencerCNC] = useState([]);
  const [tareasVencidasCNC, setTareasVencidasCNC] = useState([]);
  const [tareasVencerProbidad, setTareasVencerProbidad] = useState([]);
  const [tareasVencidasProbidad, setTareasVencidasProbidad] = useState([]);
  const [activeTab, setActiveTab] = useState('asignadas');

  useEffect(() => {
    fetchBuzonData();
  }, []);

  const fetchBuzonData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      
      // Obtener todas las gestiones
      const response = await axios.get(`${API}/gestiones`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const gestiones = response.data || [];
      const hoy = new Date();
      hoy.setHours(0, 0, 0, 0); // Inicio del día
      const enSieteDias = new Date();
      enSieteDias.setDate(enSieteDias.getDate() + 7);

      // Filtrar gestiones asignadas al usuario actual
      const asignadas = gestiones.filter(g => 
        (g.nombre_asesor_cnc === user?.full_name || 
         g.nombre_asesor_cnc === user?.email) &&
        g.estado !== 'Completada / Archivada'
      );
      setGestionesAsignadas(asignadas);

      // Tareas por vencer para Asesor CNC (fecha_vencimiento)
      // Solo mostrar si NO ha sido derivada a Probidad
      const vencerCNC = gestiones.filter(g => {
        if (g.estado === 'Completada / Archivada') return false;
        if (!g.fecha_vencimiento) return false;
        // Si ya fue derivada a Probidad, no mostrar como pendiente CNC
        if (g.asesor_probidad_id || g.asesor_probidad_nombre) return false;
        const fechaVenc = new Date(g.fecha_vencimiento);
        return fechaVenc <= enSieteDias && fechaVenc >= hoy;
      });
      setTareasVencerCNC(vencerCNC);

      // Tareas VENCIDAS CNC (ya pasó la fecha_vencimiento)
      // Solo mostrar si NO ha sido derivada a Probidad (si ya se derivó, fue atendida en tiempo)
      // Una gestión está vencida a partir del DÍA SIGUIENTE a la fecha de vencimiento
      const vencidasCNC = gestiones.filter(g => {
        if (g.estado === 'Completada / Archivada') return false;
        if (!g.fecha_vencimiento) return false;
        // Si ya fue derivada a Probidad, NO es vencida para CNC - fue atendida
        if (g.asesor_probidad_id || g.asesor_probidad_nombre) return false;
        const fechaVenc = parseLocalDate(g.fecha_vencimiento);
        if (!fechaVenc) return false;
        // Vencida si hoy es DESPUÉS del día de vencimiento (no el mismo día)
        return hoy > fechaVenc;
      });
      setTareasVencidasCNC(vencidasCNC);

      // Tareas por vencer de Instancia de Probidad (fecha_vencimiento_probidad)
      const vencerProbidad = gestiones.filter(g => {
        if (g.estado === 'Completada / Archivada') return false;
        if (!g.fecha_vencimiento_probidad) return false;
        const fechaVenc = parseLocalDate(g.fecha_vencimiento_probidad);
        if (!fechaVenc) return false;
        return fechaVenc <= enSieteDias && fechaVenc >= hoy;
      });
      setTareasVencerProbidad(vencerProbidad);
      
      // Tareas VENCIDAS de Instancia de Probidad (ya pasó la fecha)
      // Una gestión está vencida a partir del DÍA SIGUIENTE a la fecha de vencimiento
      const vencidasProbidad = gestiones.filter(g => {
        if (g.estado === 'Completada / Archivada') return false;
        if (!g.fecha_vencimiento_probidad) return false;
        const fechaVenc = parseLocalDate(g.fecha_vencimiento_probidad);
        if (!fechaVenc) return false;
        // Vencida si hoy es DESPUÉS del día de vencimiento (no el mismo día)
        return hoy > fechaVenc;
      });
      setTareasVencidasProbidad(vencidasProbidad);

    } catch (error) {
      console.error('Error al cargar datos del buzón:', error);
      toast.error('Error al cargar notificaciones');
    } finally {
      setLoading(false);
    }
  };

  const getDiasRestantes = (fecha) => {
    const hoy = new Date();
    const fechaVenc = new Date(fecha);
    const diffTime = fechaVenc - hoy;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const getUrgenciaColor = (dias) => {
    if (dias <= 0) return '#D32F2F'; // Vencido - rojo
    if (dias <= 2) return '#F57C00'; // Urgente - naranja
    if (dias <= 5) return '#FBC02D'; // Próximo - amarillo
    return '#4CAF50'; // OK - verde
  };

  const handleGestionClick = (gestion) => {
    if (onSelectGestion) {
      onSelectGestion(gestion.id);
    }
    if (onClose) {
      onClose();
    }
  };

  const totalNotificaciones = gestionesAsignadas.length + tareasVencerCNC.length + tareasVencidasCNC.length + tareasVencerProbidad.length + tareasVencidasProbidad.length;

  return (
    <div className="buzon-panel" data-testid="buzon-panel">
      <div className="buzon-header">
        <div className="buzon-title">
          <Bell size={20} />
          <span>Buzón de Notificaciones</span>
          {totalNotificaciones > 0 && (
            <span className="buzon-badge">{totalNotificaciones}</span>
          )}
        </div>
        <button className="buzon-close" onClick={onClose}>
          <X size={20} />
        </button>
      </div>

      <div className="buzon-tabs" style={{ flexWrap: 'wrap' }}>
        <button 
          className={`buzon-tab ${activeTab === 'asignadas' ? 'active' : ''}`}
          onClick={() => setActiveTab('asignadas')}
        >
          <Inbox size={16} />
          Asignadas
        </button>
        <button 
          className={`buzon-tab ${activeTab === 'vencer-cnc' ? 'active' : ''}`}
          onClick={() => setActiveTab('vencer-cnc')}
        >
          <Clock size={16} />
          Por Vencer CNC
        </button>
        <button 
          className={`buzon-tab ${activeTab === 'vencidas-cnc' ? 'active' : ''}`}
          onClick={() => setActiveTab('vencidas-cnc')}
          style={{ color: tareasVencidasCNC.length > 0 ? '#D32F2F' : undefined }}
        >
          <AlertTriangle size={16} />
          Vencidas CNC
        </button>
        <button 
          className={`buzon-tab ${activeTab === 'vencer-probidad' ? 'active' : ''}`}
          onClick={() => setActiveTab('vencer-probidad')}
        >
          <AlertTriangle size={16} />
          Por Vencer IP
        </button>
        <button 
          className={`buzon-tab ${activeTab === 'vencidas-probidad' ? 'active' : ''}`}
          onClick={() => setActiveTab('vencidas-probidad')}
          style={{ color: tareasVencidasProbidad.length > 0 ? '#D32F2F' : undefined }}
        >
          <AlertTriangle size={16} />
          Vencidas IP
        </button>
      </div>

      <div className="buzon-content">
        {loading ? (
          <div className="buzon-loading">
            <div className="loader"></div>
            <p>Cargando notificaciones...</p>
          </div>
        ) : (
          <>
            {activeTab === 'asignadas' && (
              <div className="buzon-list">
                {gestionesAsignadas.length === 0 ? (
                  <div className="buzon-empty">
                    <CheckCircle size={32} color="#4CAF50" />
                    <p>No tiene gestiones pendientes asignadas</p>
                  </div>
                ) : (
                  gestionesAsignadas.map(gestion => (
                    <div 
                      key={gestion.id} 
                      className="buzon-item"
                      onClick={() => handleGestionClick(gestion)}
                    >
                      <div className="buzon-item-icon">
                        <User size={16} />
                      </div>
                      <div className="buzon-item-content">
                        <div className="buzon-item-title">{gestion.nomenclatura}</div>
                        <div className="buzon-item-desc">{gestion.institucion}</div>
                        <div className="buzon-item-meta">
                          <span className={`status-badge status-${gestion.estado?.toLowerCase().replace(/\s+/g, '-')}`}>
                            {gestion.estado}
                          </span>
                        </div>
                      </div>
                      <ChevronRight size={16} className="buzon-item-arrow" />
                    </div>
                  ))
                )}
              </div>
            )}

            {activeTab === 'vencer-cnc' && (
              <div className="buzon-list">
                {tareasVencerCNC.length === 0 ? (
                  <div className="buzon-empty">
                    <CheckCircle size={32} color="#4CAF50" />
                    <p>No hay tareas próximas a vencer</p>
                  </div>
                ) : (
                  tareasVencerCNC.map(gestion => {
                    const dias = getDiasRestantes(gestion.fecha_vencimiento);
                    return (
                      <div 
                        key={gestion.id} 
                        className="buzon-item"
                        onClick={() => handleGestionClick(gestion)}
                        style={{ borderLeftColor: getUrgenciaColor(dias) }}
                      >
                        <div className="buzon-item-icon" style={{ color: getUrgenciaColor(dias) }}>
                          <Calendar size={16} />
                        </div>
                        <div className="buzon-item-content">
                          <div className="buzon-item-title">{gestion.nomenclatura}</div>
                          <div className="buzon-item-desc">{gestion.institucion}</div>
                          <div className="buzon-item-meta">
                            <span style={{ color: getUrgenciaColor(dias), fontWeight: 600 }}>
                              {dias <= 0 ? 'VENCIDO' : `Vence en ${dias} días`}
                            </span>
                          </div>
                        </div>
                        <ChevronRight size={16} className="buzon-item-arrow" />
                      </div>
                    );
                  })
                )}
              </div>
            )}

            {/* Pestaña: Gestiones VENCIDAS CNC */}
            {activeTab === 'vencidas-cnc' && (
              <div className="buzon-list">
                {tareasVencidasCNC.length === 0 ? (
                  <div className="buzon-empty">
                    <CheckCircle size={32} color="#4CAF50" />
                    <p>No hay gestiones vencidas CNC</p>
                  </div>
                ) : (
                  tareasVencidasCNC.map(gestion => {
                    const fechaVenc = new Date(gestion.fecha_vencimiento);
                    const hoy = new Date();
                    const diasVencido = Math.abs(Math.ceil((hoy - fechaVenc) / (1000 * 60 * 60 * 24)));
                    
                    return (
                      <div 
                        key={gestion.id} 
                        className="buzon-item"
                        onClick={() => handleGestionClick(gestion)}
                        style={{ borderLeftColor: '#D32F2F', background: '#FFEBEE' }}
                      >
                        <div className="buzon-item-icon" style={{ color: '#D32F2F' }}>
                          <AlertTriangle size={16} />
                        </div>
                        <div className="buzon-item-content">
                          <div className="buzon-item-title" style={{ color: '#D32F2F' }}>{gestion.nomenclatura}</div>
                          <div className="buzon-item-desc">
                            <strong>Asesor CNC:</strong> {gestion.nombre_asesor_cnc || 'No asignado'}
                          </div>
                          <div className="buzon-item-desc">
                            <strong>Institución:</strong> {gestion.institucion || 'N/A'}
                          </div>
                          <div className="buzon-item-meta">
                            <span style={{ color: '#D32F2F', fontWeight: 700 }}>
                              VENCIDO hace {diasVencido} día{diasVencido !== 1 ? 's' : ''}
                            </span>
                            <span style={{ fontSize: '0.7rem', color: '#666', marginLeft: '0.5rem' }}>
                              (Venció: {fechaVenc.toLocaleDateString('es-ES')})
                            </span>
                          </div>
                        </div>
                        <ChevronRight size={16} className="buzon-item-arrow" />
                      </div>
                    );
                  })
                )}
              </div>
            )}

            {activeTab === 'vencer-probidad' && (
              <div className="buzon-list">
                {tareasVencerProbidad.length === 0 ? (
                  <div className="buzon-empty">
                    <CheckCircle size={32} color="#4CAF50" />
                    <p>No hay tareas de Instancia de Probidad próximas a vencer</p>
                  </div>
                ) : (
                  tareasVencerProbidad.map(gestion => {
                    const dias = getDiasRestantes(gestion.fecha_vencimiento_probidad);
                    return (
                      <div 
                        key={gestion.id} 
                        className="buzon-item"
                        onClick={() => handleGestionClick(gestion)}
                        style={{ borderLeftColor: getUrgenciaColor(dias) }}
                      >
                        <div className="buzon-item-icon" style={{ color: getUrgenciaColor(dias) }}>
                          <AlertTriangle size={16} />
                        </div>
                        <div className="buzon-item-content">
                          <div className="buzon-item-title">{gestion.nomenclatura}</div>
                          <div className="buzon-item-desc">
                            <strong>IP:</strong> {gestion.asesor_probidad_nombre || 'No asignado'}
                          </div>
                          <div className="buzon-item-meta">
                            <span style={{ color: getUrgenciaColor(dias), fontWeight: 600 }}>
                              {dias <= 0 ? 'VENCIDO' : `Vence en ${dias} días`}
                            </span>
                          </div>
                        </div>
                        <ChevronRight size={16} className="buzon-item-arrow" />
                      </div>
                    );
                  })
                )}
              </div>
            )}

            {/* Pestaña: Gestiones VENCIDAS de Instancia de Probidad */}
            {activeTab === 'vencidas-probidad' && (
              <div className="buzon-list">
                {tareasVencidasProbidad.length === 0 ? (
                  <div className="buzon-empty">
                    <CheckCircle size={32} color="#4CAF50" />
                    <p>No hay gestiones vencidas de Instancia de Probidad</p>
                  </div>
                ) : (
                  tareasVencidasProbidad.map(gestion => {
                    const fechaVenc = new Date(gestion.fecha_vencimiento_probidad);
                    const hoy = new Date();
                    const diasVencido = Math.abs(Math.ceil((hoy - fechaVenc) / (1000 * 60 * 60 * 24)));
                    
                    return (
                      <div 
                        key={gestion.id} 
                        className="buzon-item"
                        onClick={() => handleGestionClick(gestion)}
                        style={{ borderLeftColor: '#D32F2F', background: '#FFEBEE' }}
                      >
                        <div className="buzon-item-icon" style={{ color: '#D32F2F' }}>
                          <AlertTriangle size={16} />
                        </div>
                        <div className="buzon-item-content">
                          <div className="buzon-item-title" style={{ color: '#D32F2F' }}>{gestion.nomenclatura}</div>
                          <div className="buzon-item-desc">
                            <strong>IP:</strong> {gestion.asesor_probidad_nombre || 'No asignado'}
                          </div>
                          <div className="buzon-item-desc">
                            <strong>Institución:</strong> {gestion.institucion || 'N/A'}
                          </div>
                          <div className="buzon-item-meta">
                            <span style={{ color: '#D32F2F', fontWeight: 700 }}>
                              VENCIDO hace {diasVencido} día{diasVencido !== 1 ? 's' : ''}
                            </span>
                            <span style={{ fontSize: '0.7rem', color: '#666', marginLeft: '0.5rem' }}>
                              (Venció: {fechaVenc.toLocaleDateString('es-ES')})
                            </span>
                          </div>
                        </div>
                        <ChevronRight size={16} className="buzon-item-arrow" />
                      </div>
                    );
                  })
                )}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default Buzon;
